import { Badge, Circle, Flex, Icon, Text } from '@chakra-ui/react';
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { LuChevronRight } from 'react-icons/lu';
import { AdminInhabitant } from 'src/api/Admin';
import type { Role } from 'src/api/Role';
import { OrgUtil } from 'src/data/OrgUtil';
import { useI18n } from 'src/i18n/I18nContext';
import { Button } from '@/components/ui/button';
import type { ButtonProps } from '@/components/ui/button';
import { MenuItem } from '@/components/ui/menu';
import { ChevronDownIcon } from '../icons/Icon';
import {
    HeaderLinkNode,
    HeaderMenuNode,
    isMenuLinkActive,
} from './navigation';

export const HeaderNavigationBadge = ({ count }: { count?: number }) => {
    if (!count || count < 1) return null;

    return (
        <Badge colorPalette='red' rounded='full' minW='1.5rem' justifyContent='center'>
            {count > 99 ? '99+' : count}
        </Badge>
    );
};

const OrganizationAdminBadge = ({ node, role }: { node: HeaderMenuNode; role?: Role }) => {
    if (node.link !== '/organization' || !OrgUtil.isAllowed(role, AdminInhabitant)) {
        return null;
    }

    return <Badge colorPalette='red' rounded='full' ms='2'>admin</Badge>;
};

interface DesktopNavButtonProps extends ButtonProps {
    active: boolean;
    children: ReactNode;
    showChevron?: boolean;
}

export const DesktopNavButton = forwardRef<HTMLButtonElement, DesktopNavButtonProps>(
    function DesktopNavButton({
        active,
        children,
        showChevron = true,
        ...props
    }, ref) {
        return (
            <Button
                ref={ref}
                variant='ghost'
                size='md'
                rounded='lg'
                px='3.5'
                gap='1.5'
                fontWeight='semibold'
                color={active ? 'blue.contrast' : 'fg.muted'}
                bg={active ? 'blue.solid' : 'transparent'}
                boxShadow={active ? '0 6px 18px -8px var(--chakra-colors-blue-600)' : 'none'}
                _hover={{
                    bg: active ? 'blue.emphasized' : 'bg.emphasized',
                    color: active ? 'blue.contrast' : 'fg',
                }}
                transition='background 160ms ease, color 160ms ease, box-shadow 160ms ease'
                {...props}
            >
                {children}
                {showChevron && <ChevronDownIcon />}
            </Button>
        );
    }
);

export const HeaderMenuIcon = ({
    node,
    active = false,
    adminColor = false,
}: {
    node: HeaderMenuNode;
    active?: boolean;
    adminColor?: boolean;
}) => (
    <Flex
        boxSize='9'
        align='center'
        justify='center'
        rounded='lg'
        flexShrink={0}
        bg={active ? 'blue.solid' : adminColor ? 'red.subtle' : 'bg.subtle'}
        color={active ? 'blue.contrast' : adminColor ? 'red.fg' : 'fg.muted'}
    >
        {node.icon
            ? <Icon boxSize='5' as={node.icon} />
            : <Icon boxSize='4' as={LuChevronRight} />}
    </Flex>
);

export const HeaderDropdownItem = ({
    node,
    role,
    path,
    badgeCount,
    onNavigate,
}: {
    node: HeaderLinkNode;
    role?: Role;
    path: string;
    badgeCount?: number;
    onNavigate: (link: string) => void;
}) => {
    const { t } = useI18n();
    const active = isMenuLinkActive(node.link, path);
    const adminColor = OrgUtil.isRoleAdmin(node.expectedRole) || OrgUtil.isRoleRoot(node.expectedRole);

    return (
        <MenuItem
            value={node.link}
            onClick={() => onNavigate(node.link)}
            rounded='lg'
            px='2.5'
            py='2'
            gap='3'
            color={adminColor && !active ? 'red.fg' : 'fg'}
            bg={active ? 'blue.subtle' : 'transparent'}
            _hover={{ bg: active ? 'blue.muted' : 'bg.subtle' }}
        >
            <HeaderMenuIcon node={node} active={active} adminColor={adminColor} />
            <Text flex='1' fontWeight={active ? 'semibold' : 'medium'}>
                {t(node.nameKey)}
            </Text>
            <HeaderNavigationBadge count={badgeCount} />
            <OrganizationAdminBadge node={node} role={role} />
            {active && <Circle size='1.5' bg='blue.solid' />}
        </MenuItem>
    );
};

export const MobileNavLink = ({
    node,
    role,
    path,
    badgeCount,
    nested = false,
    onNavigate,
}: {
    node: HeaderLinkNode;
    role?: Role;
    path: string;
    badgeCount?: number;
    nested?: boolean;
    onNavigate: (link: string) => void;
}) => {
    const { t } = useI18n();
    const active = isMenuLinkActive(node.link, path);
    const adminColor = OrgUtil.isRoleAdmin(node.expectedRole) || OrgUtil.isRoleRoot(node.expectedRole);

    return (
        <Button
            variant='ghost'
            width='full'
            minH='12'
            height='auto'
            justifyContent='flex-start'
            rounded='xl'
            px={nested ? '2.5' : '3'}
            gap='3'
            color={active ? 'blue.fg' : adminColor ? 'red.fg' : 'fg'}
            bg={active ? 'blue.subtle' : 'transparent'}
            _hover={{ bg: active ? 'blue.muted' : 'bg.subtle' }}
            aria-current={active ? 'page' : undefined}
            onClick={() => onNavigate(node.link)}
        >
            <HeaderMenuIcon node={node} active={active} adminColor={adminColor} />
            <Text flex='1' textAlign='left' fontWeight={active ? 'semibold' : 'medium'}>
                {t(node.nameKey)}
                <OrganizationAdminBadge node={node} role={role} />
            </Text>
            <HeaderNavigationBadge count={badgeCount} />
            <Icon boxSize='4' color='fg.subtle' as={LuChevronRight} />
        </Button>
    );
};
